// app/javascript/controllers/load_more_messages_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["button", "loader", "error"]
    static values = {
        url: String,
        initialEndDate: { type: String, default: new Date().toISOString() },
        hasMore: { type: Boolean, default: true }
    }

    connect() {
        this.isLoading = false
        this.endDate = this.initialEndDateValue
        this.earliestDate = null
        this.observer = this.setupIntersectionObserver()
    }

    disconnect() {
        this.observer?.disconnect()
    }

    async loadMore(event) {
        if (event) event.preventDefault()
        if (this.isLoading || !this.hasMoreValue) return

        this.startLoading()

        try {
            const response = await fetch(`${this.data.get("url")}`, {
                headers: {
                    "Accept": "text/vnd.turbo-stream.html",
                    "X-Requested-With": "XMLHttpRequest"
                }
            })

            if (!response.ok) throw new Error(response.statusText || "Failed to load messages")

            const contentType = response.headers.get("content-type")
            if (contentType.includes("turbo-stream")) {
                await this.handleTurboStreamResponse(response)
            } else {
                await this.handleHTMLResponse(response)
            }

            this.updatePaginationState(response)

        } catch (error) {
            console.error("Message loading error:", error)
            this.showError(error.message)
            this.retryLoading()
        } finally {
            this.stopLoading()
        }
    }

    // Private methods

    setupIntersectionObserver() {
        const options = {
            rootMargin: "200px",
            threshold: 0.1
        }

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting && !this.isLoading && this.hasMoreValue) {
                    this.loadMore()
                }
            })
        }, options)

        observer.observe(this.buttonTarget)
        return observer
    }

    async handleTurboStreamResponse(response) {
        const html = await response.text()
        const fragment = document.createRange().createContextualFragment(html)
        this.extractEarliestDateFromTurboStream(fragment)

        const scrollContainer = this.element.closest("[data-controller='messages-scroll']")
        const messagesList = document.getElementById("messages-list")
        const prevHeight = messagesList ? messagesList.scrollHeight : 0
        const prevScroll = scrollContainer ? scrollContainer.scrollTop : 0

        document.documentElement.appendChild(fragment)

        if (scrollContainer && messagesList) {
            requestAnimationFrame(() => {
                const newHeight = messagesList.scrollHeight
                scrollContainer.scrollTop = prevScroll + (newHeight - prevHeight)
            })
        }
    }

    async handleHTMLResponse(response) {
        const html = await response.text()
        const messagesContainer = document.getElementById("messages-list")
        messagesContainer.insertAdjacentHTML("beforeend", html)
        this.extractEarliestDateFromHTML(html)
    }

    extractEarliestDateFromTurboStream(fragment) {
        // Find all append operations in the Turbo Stream
        const appendOperations = fragment.querySelectorAll('turbo-stream[action="prepend"]')
        let allDates = []

        appendOperations.forEach(operation => {
            const template = operation.querySelector('template')
            if (template) {
                const tempDiv = document.createElement('div')
                tempDiv.innerHTML = template.innerHTML
                const messages = tempDiv.querySelectorAll('[data-message-date]')
                messages.forEach(msg => {
                    allDates.push(msg.dataset.messageDate)
                })
            }
        })

        if (allDates.length > 0) {
            this.earliestDate = allDates.sort()[0] // Get the oldest date
        }else{
            this.earliestDate = ""
        }
    }

    extractEarliestDateFromHTML(html) {
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = html
        const messages = tempDiv.querySelectorAll('[data-message-date]')
        if (messages.length > 0) {
            this.earliestDate = Array.from(messages)
                .map(msg => msg.dataset.messageDate)
                .sort()[0]
        }
    }

    updatePaginationState(response) {
        if (this.earliestDate) {
            this.endDate = this.earliestDate
        }else{
            this.buttonTarget.classList.add("hidden")
        }
    }

    startLoading() {
        this.isLoading = true
        this.buttonTarget.disabled = true
        this.loaderTarget.classList.remove("hidden")
        this.errorTarget.classList.add("hidden")
        this.buttonTarget.setAttribute("aria-busy", "true")
    }

    stopLoading() {
        this.isLoading = false
        this.buttonTarget.disabled = false
        this.loaderTarget.classList.add("hidden")
        this.buttonTarget.removeAttribute("aria-busy")
    }

    showError(message) {
        this.errorTarget.textContent = message
        this.errorTarget.classList.remove("hidden")
        this.buttonTarget.classList.remove("hidden")
    }

    retryLoading() {
        setTimeout(() => {
            if (!this.isLoading && this.hasMoreValue) {
                this.loadMore()
            }
        }, 3000)
    }
}
